import React, { useMemo, useState } from 'react';
import { AccordionSummary } from '@mui/material';
import { AccordionDetails } from '@mui/material';
import { BorderlessAccordion } from 'apps/sigma2-ui/src/components/ui/BorderlessAccordion';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface ExpandableExplanationProps {
  headline?: string;
  overview: string;
  details: string;
  expandButtonLabels?: {
    show: string;
    hide: string;
  };
  className?: string;
  disableBorder?: boolean;
}

export const ExpandableExplanation: React.FC<ExpandableExplanationProps> = ({
  headline,
  overview,
  details,
  expandButtonLabels,
  className,
  disableBorder = false,
}) => {
  const language = useLanguage();

  const translatedExpandButtonLabels = useMemo(() => {
    return (
      expandButtonLabels ?? {
        show: language.showExplanationDetails(),
        hide: language.hideExplanationDetails(),
      }
    );
  }, [expandButtonLabels, language]);

  const [isOverviewAnalysisExpanded, setIsOverviewAnalysisExpanded] =
    useState(false);

  const hasDetails = useMemo(() => details.trim() !== '', [details]);

  return (
    <div className={className}>
      {headline !== undefined && (
        <S2Typography
          as={'h5'}
          variant={'headline-5-alt'}
          textColorVariant={'royal-800'}
          translatedContent={headline}
          dangerouslyOverrideClassNameFn={(cn) => `${cn} pb-4`}
        />
      )}
      <S2Typography
        variant={'body-6'}
        textColorVariant={'gray-800'}
        translatedContent={overview}
      />
      {hasDetails && (
        <BorderlessAccordion
          className="w-fit flex-col-reverse"
          toggleAtBottom={true}
          expanded={isOverviewAnalysisExpanded}
          onChange={(_, expanded) => setIsOverviewAnalysisExpanded(expanded)}
        >
          <AccordionSummary className="p-0 w-fit">
            <S2Typography
              variant={'body-6-bold'}
              textColorVariant={'royal-800'}
              translatedContent={
                isOverviewAnalysisExpanded
                  ? translatedExpandButtonLabels.hide
                  : translatedExpandButtonLabels.show
              }
            />
          </AccordionSummary>
          <AccordionDetails className="p-0 pt-1">
            <S2Typography
              variant={'body-6'}
              textColorVariant={'gray-800'}
              translatedContent={details}
            />
          </AccordionDetails>
        </BorderlessAccordion>
      )}
      {!disableBorder && (
        <hr className="border-0 border-t border-[#E7E8E8] mb-10 mt-6" />
      )}
    </div>
  );
};
